import { HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpResponse } from '@angular/common/http';
import { Injectable } from "@angular/core";
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { DisplayService } from '../service/display.service';
import {TokenService} from "../service/token.service";
import {AuthStateService} from "../service/auth-state.service";
import {environment} from "../../environments/environment";
import {DeviceprintService} from "../service/deviceprint.service";

@Injectable()
export class HttpRequestResponseInterceptor implements HttpInterceptor {

  constructor(
    private readonly deviceprintservice: DeviceprintService,
    private readonly displayService: DisplayService,
    private readonly authStateService: AuthStateService,
    private readonly oAuthInitializationService : TokenService,
    private readonly router: Router
  ) { }


  intercept(request: any, next: HttpHandler): Observable<HttpEvent<any>> {
    let headerObj = {};
    if (request.body) {
      // get flowstate based on action type
      let flowstate = this.displayService.flowState;
      if (flowstate) {
        headerObj['x-flow-state'] = flowstate;
      }
    }
    if (request.body && request.body.noFlowState) {
      delete headerObj['x-flow-state'];
      delete request.body.noFlowState;
    }
    if (request.headers && this.displayService.clientTransactionId) {
      headerObj['X-CLIENT-TRANSACTION-ID'] = this.displayService.clientTransactionId;
    }
    if (this.authStateService.clientAccessToken) {
      if (request.body && request.body.noToken) {
        delete request.body.noToken;
      } else {
        headerObj['Authorization'] = 'Bearer ' + this.authStateService.clientAccessToken;
      }
    }
    if (request.body && request.body.userAccessToken) {
      headerObj['Authorization'] = 'Bearer ' + this.authStateService.userAccessToken;
      delete request.body.userAccessToken;
    }
    if (request.url.includes('/MeCreds')) {
      headerObj['Authorization'] = 'Bearer ' + this.authStateService.userAccessToken;
    }
    // for token
    if (request.body && request.body.basic) {
      headerObj['Authorization'] = request.body.basic;
      delete request.body.basic;
    }
    // for token
    if (request.body && request.body.payloadTypeAttribute) {
      headerObj['Content-Type'] = 'application/x-www-form-urlencoded';
      request.body = request.body.payloadTypeAttribute;
    }
    // Broadcom manually adds Tenant header, we add in in ingress, not necessary
    // if (request.body && request.body.tenantName) {
    //   headerObj["X-TENANT-NAME"] = localStorage.getItem("sspTenant");
    //   delete request.body.tenantName;
    // }
    if (request.body && request.body.deviceTag) {
      headerObj['x-device-tag'] = this.deviceprintservice.getIARiskDeviceTagSignature();
      delete request.body.deviceTag;
    }
    if (request.body && request.body.resetFlow) {
      headerObj['x-reset-flow'] = "true";
      headerObj['x-flow-state'] = this.displayService.flowState;
      delete request.body.resetFlow;
    }
    if (request.body && request.body.rememberThisDevice) {
      headerObj['remember-this-device'] = "true";
      delete request.body.rememberThisDevice;
    }
    if (request.body && request.body.sspTDI) {
      headerObj['ssp-tdi'] = request.body.sspTDI;
      delete request.body.sspTDI;
    }
    if (request.body && request.body["LATEST-FLOW-STATE"] === "[LATEST-FLOW-STATE]") {
      headerObj['x-flow-state'] = this.displayService.flowState;
      delete request.body["LATEST-FLOW-STATE"];
    }
    // Add x-flow-state for BrandingSettings call
    if (request.url.includes("BrandingSettings") && this.displayService.flowState) {
      headerObj['x-flow-state'] = this.displayService.flowState;
    }
    if (request.url.endsWith("/idp")) {
      headerObj['x-flow-state'] = this.displayService.flowState;
      headerObj['X-CLIENT-TRANSACTION-ID'] = this.displayService.clientTransactionId;
      headerObj['idp-relay-state'] = window.location.origin + "/portal/ui/idpComplete/";
    }

    const headers = new HttpHeaders(headerObj);
    request = request.clone({ headers });

    const successHandler = (event: HttpEvent<any>) => {
      this.authStateService.setIdleTimer();
      this.displayService.lastTransaction = new Date();
      if (event instanceof HttpResponse) {
        if (event.body && event.body.flowState) {
          let eventBodyClone = JSON.parse(JSON.stringify(event.body));
          this.displayService.processResponse(eventBodyClone);
        }
      }
    };
    // x-flow: actually because there are two separate access tokens we could be using, we need to figure out which one we are using
    const errorHandler = (error) => {
      this.displayService.lastTransaction = new Date();
      if (error.status === 401 && error.error && (error.error.errorCode === "0000005" || ("" + error.error.errorMessage).toLowerCase() === "invalid access token")) {
        return this.oAuthInitializationService.getClientAccessToken()
          .pipe(switchMap((data) => {
            this.authStateService.clientAccessToken = data["access_token"];
            const newAuthReq = request.clone({
              headers: request.headers
                .set('X-CLIENT-TRANSACTION-ID', this.displayService.clientTransactionId)
                .set("Authorization", "Bearer " + data["access_token"])
            });
            return next.handle(newAuthReq).pipe(tap(successHandler));
          }));
      }
      const contentType = error.headers?.get('content-type') || '';
      this.displayService.lastErrorResponse = { status: error.status, body: error.error, contentType };
      if (contentType && !contentType.includes('application/json') && !contentType.includes('application/octet-stream')) {
        this.openNonJsonResponseInNewTab(error);
      }
      this.displayService.errorDiv = true;
      this.displayService.errorMsg = this.resolveErrorMessage(error);
      if (!(error.error instanceof ErrorEvent)) {
        if (error?.error?.errorCode == "0000010" && error?.error?.errorMessage == "Invalid X-Flow-State header") {
          this.authStateService.setAuthStatusRestartNeeded();
        }
        this.handleAuthDeniedResponse(error);
      }
      return throwError(error);
    };

    return next.handle(request).pipe(tap(successHandler), catchError(errorHandler));
  }

  /**
   * We handle AUTH_DENIED in the routeActions flow, but this can happen in other cases,
   * such as OTP strikes being hit, or too many attempts in general. So, need to catch this and display
   * a proper page to the user
  */
  private resolveErrorMessage(error): string {
    if (error.error instanceof ErrorEvent) {
      return `Unexpected Error: ${error.error.error}`;
    }
    if (error?.error?.errorMessage) { return error.error.errorMessage; }
    if (error?.error?.error_description) { return error.error.error_description; }
    if (error?.error?.data?.errorMessage) { return error.error.data.errorMessage; }
    if (error?.error?.data?.message) { return error.error.data.message; }
    return '';
  }

  private handleAuthDeniedResponse(error) {
    if (error?.error?.nextaction == "AUTH_DENIED") {
      this.authStateService.setAuthStatusNone();
      this.router.navigate(["denied"]);
    }
  }

  private openNonJsonResponseInNewTab(error: any) {
    const key = 'network-block-' + Date.now();
    const diagnostic = {
      timestamp: this.displayService.lastTransaction?.toISOString() || new Date().toISOString(),
      clientTxnId: this.displayService.clientTransactionId || '',
      userName: this.displayService.userName || '',
      responseStatus: error.status
    };
    const responseBody = error.error;
    const bodyContent = typeof responseBody === 'string' ? responseBody : JSON.stringify(responseBody, null, 2);

    localStorage.setItem(key, JSON.stringify({ diagnostic, body: bodyContent }));

    const url = new URL('assets/network-block.html', document.baseURI);
    url.searchParams.set('key', key);
    window.open(url.href, '_blank', 'noopener,noreferrer');
  }
}
